// LetterDropEffect.js
//
// "Raining letters" effect. Once Home.js has finished loading its first
// batch of events (signaled via the same `homeDataReady` event/flag App.js
// uses for the loading-screen handoff -- see Home.js's setupEventsListener),
// this component live-subscribes (onSnapshot, not a one-time fetch) to the
// `letters` collection. If the signed-in user has any unread letter
// matching their audience segment -- WeChat users, WeChat users with no
// email, users with an unverified email, everyone, or a hand-picked list of
// specific players -- it starts raining: a continuous stream of decorative
// envelope/letter emoji fall from the top and fade out near the bottom,
// indefinitely, while the one REAL letter grows in the center of the
// screen, pulsing gently, until the user taps it. Tapping stops the rain,
// fires a small firework burst, then opens the letter in a reading card.
//
// Live sync means an admin sending a new letter via LetterManager.js
// appears for matching users immediately, with no page refresh needed --
// this is why `letters` is subscribed with onSnapshot rather than fetched
// once on mount.
//
// Mount this once, near the top level of the authenticated app (the same
// place AddToHomeScreenModal / WaitlistNotificationModal live in App.js):
//
//   import LetterDropEffect from './components/LetterDropEffect';
//   ...
//   <LetterDropEffect />
//
// It reads `currentUser` itself via useAuth(), so no props are required.
//
// Firestore collections used:
//   `letters`      -- live-subscribed here; written by LetterManager.js (admin side)
//   `letterReads`  -- this component writes one doc per (letter, user) pair
//                    the first time that user opens that letter, doc id
//                    `${letterId}_${userId}`, so it's naturally idempotent
//                    and also doubles as the "already shown" guard.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';

// Mirrors LetterManager.js's matchesAudience exactly. Duplicated rather than
// imported because LetterManager.js lives in the admin-only part of the
// bundle -- keeping this component independent means the raining-letters
// effect (which every user loads) doesn't pull in any admin-side code.
//
// isWechatUser() does NOT rely solely on user.provider === 'wechat'.
// Firebase's signInWithCustomToken (the auth method this app's WeChat
// login uses -- see getWechatAuthUrl/signInWithCustomToken in firebase.js)
// leaves Auth's providerData empty, so createUserProfile's fallback writes
// provider: 'email' for WeChat users too -- they were never actually
// distinguishable by that field alone. Checking for the WeChat-specific
// identity fields (only ever populated for a real WeChat login, regardless
// of what provider says) makes this correct either way.
function isWechatUser(user) {
  return user?.provider === 'wechat' || !!user?.wechatId || !!user?.wechatOpenId || !!user?.wechatNickname;
}

function matchesAudience(user, audience, targetUserIds) {
  switch (audience) {
    case 'specific':
      return Array.isArray(targetUserIds) && targetUserIds.includes(user?.uid);
    case 'wechat_all':
      return isWechatUser(user);
    case 'wechat_no_email':
      return isWechatUser(user) && !user?.email;
    case 'email_unverified':
      return !!user?.email && !user?.emailVerified;
    case 'all':
    default:
      return true;
  }
}

// Resolves a display name for the currently signed-in viewer, trying the
// most likely fields in order. Falls back to a generic, still-warm greeting
// target rather than something robotic like "User" if nothing is on file
// (e.g. a brand new WeChat sign-in with no nickname synced yet).
function resolveViewerName(userProfile, currentUser) {
  return (
    userProfile?.name ||
    userProfile?.wechatNickname ||
    currentUser?.displayName ||
    'there'
  );
}

// Formats an ISO date string as "Jan 28, 2026" for the sign-off date line.
// Falls back to today's date if the letter has no createdAt for some
// reason, rather than showing nothing.
function formatSignoffDate(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

const DEFAULT_EMOJI = '💌';
const DECORATIVE_EMOJI_POOL = ['💌', '✉️', '📩', '📨', '💛', 
'🌸', // wave hello
'✨', // group fun
'🎉', // social event
'🌟', // community highlight
];

// How often a fresh decorative envelope spawns while it's raining. Lower =
// denser rain. Each spawned drop runs its own fall+fade animation and
// removes itself from state when done (see DecorativeRain below).
const SPAWN_INTERVAL_MS = 350;

// Small pause after Home.js signals ready before rain can start -- purely
// so the very first thing a user sees isn't already letters falling.
const POST_HOME_READY_DELAY_MS = 600;

export default function LetterDropEffect() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const viewerName = resolveViewerName(userProfile, currentUser);

  // Gate: don't subscribe to letters at all until Home.js has finished
  // loading. Without this, the rain effect and Home's own data fetch race
  // each other with no coordination.
  //
  // FALLBACK: also force this true after a hard timeout, regardless of
  // whether homeDataReady ever fires. This exists because letters were
  // reported as never showing inside the WeChat in-app browser specifically
  // — WeChat's webview is already known (see isWechatBrowser() usages in
  // App.js) to behave differently around storage and timing versus a normal
  // mobile browser, and if the homeDataReady signal gets lost or delayed
  // there for any reason, this component would otherwise wait forever with
  // no way to recover. Mirrors the same defensive pattern App.js already
  // uses for its own loading-screen 8-second absolute fallback.
  const HOME_READY_FALLBACK_MS = 6000;
  const [homeReady, setHomeReady] = useState(
    typeof window !== 'undefined' && window.__homeDataReady === true
  );

  useEffect(() => {
    if (homeReady) return undefined;
    if (typeof window !== 'undefined' && window.__homeDataReady === true) {
      setHomeReady(true);
      return undefined;
    }
    const onHomeReady = () => {
      setHomeReady(true);
    };
    window.addEventListener('homeDataReady', onHomeReady);
    const fallbackTimer = setTimeout(() => {
      setHomeReady(true);
    }, HOME_READY_FALLBACK_MS);
    return () => {
      window.removeEventListener('homeDataReady', onHomeReady);
      clearTimeout(fallbackTimer);
    };
  }, [homeReady]);

  // All currently-active letters matching this user's audience, live from
  // Firestore. Filtered down to "unread" ones separately below, since read
  // state can change locally (after the user opens one) without the
  // underlying letters collection itself changing.
  const [matchingLetters, setMatchingLetters] = useState([]);
  // letterId -> true/false, for ones we've confirmed are read or unread
  // (from letterReads, or just marked read locally this session).
  const [readIds, setReadIds] = useState({});
  const readChecksInFlightRef = useRef(new Set());

  // Live subscription to active letters, gated on homeReady + having a user.
  useEffect(() => {
    if (!homeReady || !currentUser?.uid) {
      return undefined;
    }

    let cancelled = false;
    const candidate = { ...(userProfile || {}), uid: currentUser.uid };
    const q = query(collection(db, 'letters'), where('active', '==', true));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        if (cancelled) return;
        const matching = [];
        snap.forEach((d) => {
          const letter = { id: d.id, ...d.data() };
          const isMatch = matchesAudience(candidate, letter.audience, letter.targetUserIds);
          if (isMatch) {
            matching.push(letter);
          }
        });
        // Oldest first -- like mail arriving in the order it was sent.
        matching.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
        setMatchingLetters(matching);
      },
      (err) => {
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [homeReady, currentUser?.uid, userProfile]);

  // Whenever the set of matching letters changes, check read-state for any
  // we haven't already checked (skips ones already known read/unread).
  useEffect(() => {
    if (!currentUser?.uid) return;
    const toCheck = matchingLetters.filter(
      (l) => !(l.id in readIds) && !readChecksInFlightRef.current.has(l.id)
    );
    if (toCheck.length === 0) return;

    toCheck.forEach((l) => readChecksInFlightRef.current.add(l.id));

    Promise.allSettled(
      toCheck.map((l) =>
        getDoc(doc(db, 'letterReads', `${l.id}_${currentUser.uid}`)).then((snap) => ({
          id: l.id,
          read: snap.exists(),
        }))
      )
    ).then((outcomes) => {
      setReadIds((prev) => {
        const next = { ...prev };
        outcomes.forEach((outcome, i) => {
          if (outcome.status === 'fulfilled') {
            next[outcome.value.id] = outcome.value.read;
          } else {
            // One letter's check failing shouldn't block the others —
            // Promise.allSettled (not Promise.all) ensures that. Log
            // clearly so any future permissions/rules issue is immediately
            // visible rather than silently stranding a letter the way the
            // old getDoc-on-a-nonexistent-doc rules bug did.
          }
        });
        return next;
      });
    }).finally(() => {
      toCheck.forEach((l) => readChecksInFlightRef.current.delete(l.id));
    });
  }, [matchingLetters, readIds, currentUser?.uid]);

  // The actual target letter for this rain session: oldest unread matching
  // letter, or null if there isn't one. Recomputes live as Firestore pushes
  // updates -- if an admin sends a new letter while rain is already
  // happening for an older one, this stays on the older one (oldest-first);
  // the new one takes over once the current one is read.
  const targetLetter = useMemo(() => {
    return matchingLetters.find((l) => readIds[l.id] === false) || null;
  }, [matchingLetters, readIds]);

  // Small cosmetic delay after Home becomes ready, so rain doesn't feel
  // like it's racing the page itself the instant cards appear.
  const [delayElapsed, setDelayElapsed] = useState(false);
  useEffect(() => {
    if (!homeReady) return undefined;
    const t = setTimeout(() => setDelayElapsed(true), POST_HOME_READY_DELAY_MS);
    return () => clearTimeout(t);
  }, [homeReady]);

  // 'raining' -> 'opening' (firework + grow) -> 'open' (reading card) -> 'closing'
  const [phase, setPhase] = useState('raining');
  const prevTargetIdRef = useRef(null);
  useEffect(() => {
    // Reset to 'raining' whenever a NEW target letter becomes active --
    // covers both "first letter ever" and "moved on to the next one after
    // the previous was read".
    if (targetLetter && targetLetter.id !== prevTargetIdRef.current) {
      prevTargetIdRef.current = targetLetter.id;
      setPhase('raining');
    }
    if (!targetLetter) {
      prevTargetIdRef.current = null;
    }
  }, [targetLetter]);

  const markAsRead = useCallback(async (letter) => {
    if (!currentUser?.uid || !letter) return;
    try {
      await setDoc(doc(db, 'letterReads', `${letter.id}_${currentUser.uid}`), {
        letterId: letter.id,
        userId: currentUser.uid,
        readAt: new Date().toISOString(),
      });
      setReadIds((prev) => ({ ...prev, [letter.id]: true }));
    } catch (err) {
    }
  }, [currentUser?.uid]);

  const handleTapLetter = useCallback(() => {
    if (phase !== 'raining') return;
    setPhase('opening'); // triggers firework + grow-and-fade animation
  }, [phase]);

  const handleOpeningAnimationDone = useCallback(() => {
    setPhase('open');
  }, []);

  // Safety net: if animationend never fires for any reason (reduced-motion
  // settings, a backgrounded tab skipping rAF/animation callbacks, or any
  // other browser quirk), don't let the user get stuck staring at the
  // growing animation forever with no way to actually read the letter.
  useEffect(() => {
    if (phase !== 'opening') return undefined;
    const t = setTimeout(() => {
      setPhase((p) => (p === 'opening' ? 'open' : p));
    }, 1200); // comfortably longer than the 0.7s grow animation + firework
    return () => clearTimeout(t);
  }, [phase]);

  const handleClose = useCallback(() => {
    setPhase('closing');
    markAsRead(targetLetter);
    // Just let the closing animation play — moving phase back to 'raining'
    // is handled by the targetLetter-change effect above, which fires once
    // readIds actually updates (after markAsRead's Firestore write
    // resolves) and targetLetter becomes null or the next letter. Using a
    // fixed timeout here instead would risk flipping back to 'raining'
    // for the SAME letter if the write happens to take longer than the
    // timeout on a slow connection — a real glitch, not a cosmetic one.
  }, [targetLetter, markAsRead]);

  // CTA button tap — for an in-app destination, navigates within the app
  // (react-router's navigate()); for an external link, opens a new tab via
  // window.open instead, since navigate() only understands in-app paths and
  // would otherwise mangle a full "https://…" URL into a broken relative
  // route. Either way, unless the admin chose to keep "Got it!" as a
  // separate dismiss action, this also marks the letter read and closes it,
  // exactly like tapping "Got it!" would. When dismissAlongside is true, the
  // letter stays open after navigating — for an in-app destination that's
  // moot since navigating away unmounts this component's modal anyway, but
  // for an external link (which opens in a NEW tab, leaving this tab right
  // where it was) it actually matters: the reader can tap the external link
  // and still see the letter open behind it, and only dismiss explicitly.
  const handleCtaClick = useCallback(() => {
    const cta = targetLetter?.cta;
    if (!cta?.destination) return;
    if (!cta.dismissAlongside) {
      setPhase('closing');
      markAsRead(targetLetter);
    }
    if (cta.isExternal) {
      window.open(cta.destination, '_blank', 'noopener,noreferrer');
    } else {
      navigate(cta.destination);
    }
  }, [targetLetter, markAsRead, navigate]);

  const isRaining = homeReady && delayElapsed && !!targetLetter;

  if (!isRaining && phase === 'raining') return null;
  if (!targetLetter) return null; // closing/opening with nothing active shouldn't render

  const emoji = targetLetter.emoji || DEFAULT_EMOJI;

  return (
    <>
      {phase === 'raining' && (
        <>
          <DecorativeRain />
          <CenterLetter emoji={emoji} onTap={handleTapLetter} />
        </>
      )}

      {phase === 'opening' && (
        <>
          <FireworkBurst />
          <CenterLetter emoji={emoji} growing onAnimationEnd={handleOpeningAnimationDone} />
        </>
      )}

      {(phase === 'open' || phase === 'closing') && (
        <div className={`letter-modal-backdrop ${phase === 'closing' ? 'letter-modal-closing' : ''}`}>
          <div className={`letter-paper ${phase === 'closing' ? 'letter-paper-leaving' : ''}`}>
            <div className="letter-paper-scroll">
            <div className="letter-paper-content">
              <p className="letter-paper-salutation">{(targetLetter.title || 'Hello').trim()} {viewerName},</p>
              <div className="letter-paper-body">
                {(() => {
                  const lines = targetLetter.message.split('\n');
                  const paragraphs = [];
                  let pendingSectionBreak = false;
                  for (const line of lines) {
                    const trimmed = line.trim();
                    if (trimmed === '') {
                      // A blank line marks a section break before whichever
                      // real line comes next (e.g. the gap between a
                      // Chinese block and an English block) — it doesn't
                      // render as its own empty paragraph.
                      pendingSectionBreak = paragraphs.length > 0;
                      continue;
                    }
                    paragraphs.push({ text: trimmed, sectionBreakBefore: pendingSectionBreak });
                    pendingSectionBreak = false;
                  }
                  return paragraphs.map((para, i) => (
                    <p
                      key={i}
                      className={`letter-paper-paragraph ${para.sectionBreakBefore ? 'letter-paper-section-break' : ''}`}
                    >
                      {para.text}
                    </p>
                  ));
                })()}
              </div>
              <p className="letter-paper-signoff">
                — 忘仔羽球
                <br />
                Panda Badminton Club
                <br />
                <span className="letter-paper-signoff-date">{formatSignoffDate(targetLetter.createdAt)}</span>
              </p>
            </div>
            </div>
            {phase === 'open' && (() => {
              const cta = targetLetter.cta;
              const hasCta = !!(cta?.enabled && cta?.destination);
              // "Got it!" shows when there's no CTA at all (the original,
              // unchanged behaviour), OR when there is a CTA but the admin
              // chose to keep it as a separate dismiss action alongside it.
              const showGotIt = !hasCta || cta.dismissAlongside;
              return (
                <div className="letter-paper-actions">
                  {hasCta && (
                    <button onClick={handleCtaClick} className="letter-paper-cta-btn">
                      {cta.label || 'Continue'}
                    </button>
                  )}
                  {showGotIt && (
                    <button onClick={handleClose} className="letter-paper-close-btn">
                      Got it!
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <LetterDropStyles />
    </>
  );
}

// Continuously spawns decorative (non-interactive) envelope emoji that fall
// from the top and fade out near the bottom, for as long as this component
// is mounted (i.e. for as long as phase === 'raining'). Each drop manages
// its own lifecycle and removes itself from the list when its fall
// animation finishes -- this is true continuous rain, not a one-shot batch.
function DecorativeRain() {
  const [drops, setDrops] = useState([]);
  const nextIdRef = useRef(0);

  useEffect(() => {
    const spawn = () => {
      const id = nextIdRef.current++;
      const leftPercent = 6 + Math.random() * 88; // spread across nearly the full width
      const durationS = 7 + Math.random() * 2.5; // 3s-5.5s fall, varies "rain speed" per drop
      const size = 18 + Math.round(Math.random() * 14);
      const opacity = 0.35 + Math.random() * 0.4;
      const emoji = DECORATIVE_EMOJI_POOL[Math.floor(Math.random() * DECORATIVE_EMOJI_POOL.length)];
      const swayAmount = 20 + Math.random() * 14;
      const swayDir = Math.random() < 0.5 ? -1 : 1;

      setDrops((prev) => [
        ...prev,
        { id, leftPercent, durationS, size, opacity, emoji, swayAmount, swayDir },
      ]);
    };

    spawn(); // first drop immediately, don't wait a full interval
    const intervalId = setInterval(spawn, SPAWN_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  const removeDrop = useCallback((id) => {
    setDrops((prev) => prev.filter((d) => d.id !== id));
  }, []);

  return (
    <>
      {drops.map((d) => (
        <span
          key={d.id}
          aria-hidden="true"
          className="letter-rain-drop"
          style={{
            left: `${d.leftPercent}%`,
            fontSize: `${d.size}px`,
            opacity: d.opacity,
            animationDuration: `${d.durationS}s`,
            '--sway-amount': `${d.swayAmount}px`,
            '--sway-dir': d.swayDir,
          }}
          onAnimationEnd={() => removeDrop(d.id)}
        >
          {d.emoji}
        </span>
      ))}
    </>
  );
}

// The one real, tappable letter -- sits fixed in the center of the screen.
// `growing` switches it into the bigger "about to open" animation, which
// calls onAnimationEnd when that animation finishes so the parent can move
// on to phase 'open'.
function CenterLetter({ emoji, onTap, growing = false, onAnimationEnd }) {
  return (
    <button
      onClick={onTap}
      disabled={growing}
      aria-label="Open letter"
      className={`letter-center-btn ${growing ? 'letter-center-growing' : 'letter-center-idle'}`}
      style={{ pointerEvents: growing ? 'none' : 'auto' }}
      onAnimationEnd={growing ? onAnimationEnd : undefined}
    >
      <span className="letter-center-emoji">{emoji}</span>
      {!growing && <span className="letter-envelope-hint">点我拆信 · Tap to open</span>}
    </button>
  );
}

// Small celebratory firework burst -- a handful of particles that shoot
// outward from the center and fade, purely decorative (pointer-events
// none throughout). Parent only renders this during phase === 'opening',
// which is itself brief, so it's naturally removed once that phase ends.
function FireworkBurst() {
  const particles = useMemo(() => {
    const count = 16;
    const items = [];
    for (let i = 0; i < count; i++) {
      const angle = (360 / count) * i + (Math.random() * 20 - 10);
      const distance = 70 + Math.random() * 60;
      const colors = ['#f59e0b', '#ec4899', '#8b5cf6', '#10b981', '#3b82f6', '#facc15'];
      items.push({
        id: i,
        angle,
        distance,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 5 + Math.round(Math.random() * 5),
        delayMs: Math.round(Math.random() * 80),
      });
    }
    return items;
  }, []);

  return (
    <div className="letter-firework-wrap" aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.id}
          className="letter-firework-particle"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            animationDelay: `${p.delayMs}ms`,
            '--angle': `${p.angle}deg`,
            '--distance': `${p.distance}px`,
          }}
        />
      ))}
    </div>
  );
}

// Styles injected once via a plain <style> tag, matching how the rest of
// this codebase handles one-off component styling (see App.js's toast
// keyframes, AdScreenEnhanced.js's spin keyframes, etc.) rather than
// requiring a separate CSS file import.
function LetterDropStyles() {
  return (
    <style>{`
      /* ---- Continuous rain drops ---- */
      .letter-rain-drop {
        position: fixed;
        top: -40px;
        z-index: 9450;
        pointer-events: none;
        line-height: 1;
        filter: drop-shadow(0 4px 8px rgba(0,0,0,0.18));
        animation-name: letterRainFall, letterRainSway;
        animation-timing-function: linear, ease-in-out;
        animation-iteration-count: 1, infinite;
        animation-duration: inherit, 1.6s;
      }
      @keyframes letterRainFall {
        0%   { top: -40px; }
        100% { top: 100vh; }
      }
      @keyframes letterRainSway {
        0%   { margin-left: 0; }
        50%  { margin-left: calc(var(--sway-amount, 12px) * var(--sway-dir, 1)); }
        100% { margin-left: 0; }
      }

      /* ---- Center letter (the one real, tappable letter) ---- */
      .letter-center-btn {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: none;
        border: none;
        cursor: pointer;
        z-index: 9500;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        -webkit-tap-highlight-color: transparent;
      }
      .letter-center-emoji {
        font-size: 72px;
        line-height: 1;
        filter: drop-shadow(0 8px 16px rgba(0,0,0,0.3));
        display: inline-block;
      }
      .letter-center-idle .letter-center-emoji {
        animation: letterCenterPulse 1.8s ease-in-out infinite;
      }
      @keyframes letterCenterPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.12); }
      }
      .letter-center-growing .letter-center-emoji {
        animation: letterCenterGrow 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
      @keyframes letterCenterGrow {
        0%   { transform: scale(1); opacity: 1; }
        60%  { transform: scale(2.4); opacity: 1; }
        100% { transform: scale(2.1); opacity: 0; }
      }
      .letter-center-growing .letter-envelope-hint {
        display: none;
      }

      .letter-envelope-hint {
        font-size: 12px;
        font-weight: 600;
        color: #92400e;
        background: rgba(255,255,255,0.92);
        padding: 4px 12px;
        border-radius: 999px;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.12);
        animation: letterHintFade 0.4s ease-out;
      }
      @keyframes letterHintFade {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* ---- Firework burst on open ---- */
      .letter-firework-wrap {
        position: fixed;
        top: 50%;
        left: 50%;
        width: 0;
        height: 0;
        z-index: 9550;
        pointer-events: none;
      }
      .letter-firework-particle {
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 50%;
        opacity: 1;
        animation: letterFireworkShoot 0.8s ease-out forwards;
      }
      @keyframes letterFireworkShoot {
        0% {
          transform: translate(-50%, -50%) rotate(var(--angle)) translateX(0);
          opacity: 1;
        }
        100% {
          transform: translate(-50%, -50%) rotate(var(--angle)) translateX(var(--distance));
          opacity: 0;
        }
      }

      /* ---- Opened letter -- warm paper card with a soft "unfold" entrance ---- */
      .letter-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.7);
        backdrop-filter: blur(3px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9600;
        padding: 16px;
        animation: letterBackdropIn 0.3s ease-out;
      }
      .letter-modal-closing {
        animation: letterBackdropOut 0.35s ease-in forwards;
      }
      @keyframes letterBackdropIn {
        from { opacity: 0; } to { opacity: 1; }
      }
      @keyframes letterBackdropOut {
        from { opacity: 1; } to { opacity: 0; }
      }

      .letter-paper {
        position: relative;
        max-width: 420px;
        width: 100%;
        max-height: 85vh;
        /* NOT overflow-y: auto.
           The card itself must never be the scroll container, because the
           decorative frame below is an absolutely positioned child of it. In a
           scrolling box, such a child is laid out against the visible padding
           box and then scrolls away with the content — so on any letter long
           enough to scroll, the frame drifted up off the top of the card and
           the lower part of the letter had no border at all.
           Scrolling now happens on .letter-paper-scroll inside, leaving this
           element a fixed, framed card of exactly the letter's height (up to
           the 85vh cap). */
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background: #fdfaf3;
        background-image:
          repeating-linear-gradient(0deg, rgba(0,0,0,0.015) 0px, rgba(0,0,0,0.015) 1px, transparent 1px, transparent 28px);
        border-radius: 16px;
        box-shadow: 0 30px 70px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.04);
        padding: 40px 32px 32px;
        text-align: center;
        animation: letterUnfold 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      /* Decorative inner frame, like an invitation card's inset border.
         Sized off the card, so it grows with a short letter and stays put on a
         long one — always enclosing the whole visible card. */
      .letter-paper::before {
        content: '';
        position: absolute;
        inset: 10px;
        border: 1.5px solid rgba(217, 119, 6, 0.4);
        border-radius: 10px;
        pointer-events: none;
        z-index: 1;
      }

      /* The scrolling region. Sits inside the frame with a little breathing
         room so text never runs underneath the border line, and takes the
         leftover height so the action buttons below stay pinned and visible
         however long the letter is. */
      .letter-paper-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding-right: 6px;
        margin-right: -6px;
      }
      /* A slim, letter-toned scrollbar rather than the browser default, which
         reads as a jarring grey slab against the cream paper. */
      .letter-paper-scroll::-webkit-scrollbar {
        width: 5px;
      }
      .letter-paper-scroll::-webkit-scrollbar-thumb {
        background: rgba(217, 119, 6, 0.28);
        border-radius: 3px;
      }
      .letter-paper-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .letter-paper-scroll {
        scrollbar-width: thin;
        scrollbar-color: rgba(217, 119, 6, 0.28) transparent;
      }
      /* Mobile: explicit 80vw width rather than the 100%-up-to-420px cap
         above — on a narrow phone, 80vw reads as a deliberately wide,
         letter-like card with consistent margins either side, rather than
         a width that happens to land wherever the 420px cap and the
         backdrop's own padding intersect. */
      @media (max-width: 640px) {
        .letter-paper {
          width: 85vw;
          max-width: 85vw;
        }
      }
      .letter-paper-leaving {
        animation: letterFold 0.3s ease-in forwards;
      }
      @keyframes letterUnfold {
        from { opacity: 0; transform: scale(0.7) rotateX(-12deg); }
        to   { opacity: 1; transform: scale(1) rotateX(0deg); }
      }
      @keyframes letterFold {
        from { opacity: 1; transform: scale(1); }
        to   { opacity: 0; transform: scale(0.85); }
      }

      /* Letter content flows left-aligned, like an actual written letter --
         salutation line, then the body, then the sign-off -- rather than a
         large centered heading sitting apart from a separate paragraph.
         Only the Close button (outside this wrapper) stays centered. */
      .letter-paper-content {
        text-align: left;
      }
      .letter-paper-salutation {
        font-size: 19px;
        font-weight: 700;
        color: #78350f;
        margin: 0 0 16px;
        line-height: 1.4;
      }
      .letter-paper-body {
        margin: 0 0 24px;
      }
      /* Each line from the message becomes its own paragraph with real
         margin-bottom — this is what actually creates visible space
         between sentences. line-height alone only affects spacing within
         a single wrapped line; it does nothing for explicit line breaks,
         which is why sentences were reading as a dense, run-together
         block before this. */
      .letter-paper-paragraph {
        font-size: 17px;
        line-height: 1.7;
        color: #44403c;
        margin: 0 0 12px;
      }
      .letter-paper-paragraph:last-child {
        margin-bottom: 0;
      }
      /* Bigger gap above a paragraph that follows a blank line in the
         original message — e.g. the break between a Chinese block and an
         English block — so that intentional section break still reads as
         clearly larger than the normal between-sentence spacing. */
      .letter-paper-paragraph.letter-paper-section-break {
        margin-top: 16px;
      }
      .letter-paper-signoff {
        font-size: 12px;
        font-style: normal;
        color: #78716c;
        line-height: 1.6;
        margin: 0 0 20px;
        text-align: right;
      }
      .letter-paper-signoff-date {
        font-size: 11px;
        color: #a8a29e;
      }
      /* Row wrapper for the action button(s) — centers either a single
         button (just "Got it!", or just a CTA in dismissAlongside:false
         mode) or two side by side (CTA + "Got it!") without the markup
         needing to know which case it is. */
      /* Outside the scrolling region, so the buttons stay reachable on a long
         letter instead of requiring a scroll to the very bottom. */
      .letter-paper-actions {
        flex-shrink: 0;
        position: relative;
        z-index: 2;
        /* Space above, so on a long letter the buttons read as a separate
           footer rather than looking joined to the text scrolling behind. */
        padding-top: 18px;
        margin-top: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        flex-wrap: wrap;
      }
      .letter-paper-close-btn {
        background: transparent;
        color: #d97706;
        font-weight: 600;
        font-size: 13px;
        padding: 7px 20px;
        border-radius: 999px;
        border: 1.5px solid #d97706;
        cursor: pointer;
        box-shadow: none;
        transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;
        min-height: 34px;
      }
      .letter-paper-close-btn:hover {
        background: rgba(217, 119, 6, 0.08);
      }
      .letter-paper-close-btn:active {
        transform: scale(0.97);
      }
      /* CTA button — filled/solid, the visually primary action whenever a
         letter has one, vs. "Got it!"'s lighter outlined treatment. */
      .letter-paper-cta-btn {
        background: #d97706;
        color: #fffbeb;
        font-weight: 600;
        font-size: 13px;
        padding: 8px 22px;
        border-radius: 999px;
        border: 1.5px solid #d97706;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(217, 119, 6, 0.35);
        transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
        min-height: 34px;
      }
      .letter-paper-cta-btn:hover {
        background: #b45309;
      }
      .letter-paper-cta-btn:active {
        transform: scale(0.97);
      }

      @media (max-width: 400px) {
        .letter-paper {
          padding: 32px 24px 26px;
          border-radius: 14px;
        }
        .letter-paper-salutation {
          font-size: 18px;
        }
        .letter-paper-paragraph {
          font-size: 16px;
        }
        .letter-center-emoji {
          font-size: 56px;
        }
      }
    `}</style>
  );
}